/**
 * Guardas de `focusout` para os menus/dropdowns do módulo Pipeline.
 *
 * Um `focusout` esconde três situações diferentes:
 *
 * 1. O foco foi para um nó REAL fora do menu → o usuário saiu, feche.
 * 2. O foco continua dentro do menu (troca de item) → não faça nada.
 * 3. O foco não foi para lugar nenhum (`relatedTarget` nulo, navegador
 *    devolvendo o foco ao `<body>`) → ninguém saiu; o menu precisa recuperar
 *    o foco, porque as setas e o Escape estão no elemento do menu e um menu
 *    aberto sem foco é um menu morto para teclado.
 *
 * Tratar (3) como (1) fechava o menu recém-aberto ~20ms depois de ele
 * aparecer, antes de o usuário ver (regressão 2026-08-05).
 *
 * A regra final é: ou o foco volta para dentro do menu, ou o menu fecha —
 * nunca fica aberto e inalcançável.
 */

// A recuperação existe para UMA falha: a corrida em que o navegador devolve o
// foco ao `<body>` logo depois de o menu abrir. Por isso ela é única por sessão
// de abertura — uma segunda perda de foco seguida é lida como saída de verdade
// e fecha o menu, em vez de ficar puxando o foco de volta e transformando um
// menu não-modal em armadilha para quem navega por teclado.
//
// A permissão é liberada quando a sessão termina ou se prova saudável: menu
// fechado por aqui, foco confirmado de volta dentro dele, ou o componente
// avisando que abriu (`releaseMenuFocusBudget`). Cinco dos seis containers são
// recriados a cada abertura e ganham a permissão de graça; o do
// PipelineSelector persiste e por isso avisa explicitamente.
const restoreUsed = new WeakSet();

const consumeRestoreAttempt = menu => {
  if (restoreUsed.has(menu)) return false;
  restoreUsed.add(menu);
  return true;
};

const FOCUSABLE_MENU_ITEM =
  '[role="menuitem"]:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const focusIsNowhere = active =>
  !active || active === document.body || active === document.documentElement;

/**
 * `true` apenas quando o foco foi para um nó real fora do menu.
 *
 * Precisa ser chamada de forma síncrona no handler: o navegador anula
 * `event.currentTarget` assim que o despacho do evento termina.
 */
export const focusLeftMenu = event => {
  const nextTarget = event?.relatedTarget;
  const menu = event?.currentTarget;
  if (!nextTarget || typeof menu?.contains !== 'function') return false;
  return !menu.contains(nextTarget);
};

/**
 * Devolve o foco para dentro do menu, preservando o item em que o usuário
 * estava. Retorna `false` quando não foi possível — menu fora do DOM, sem item
 * focável ou orçamento esgotado —, e nesse caso quem chama deve fechar o menu.
 */
export const restoreMenuFocus = (menu, preferred = null) => {
  if (typeof menu?.querySelector !== 'function' || !menu.isConnected) {
    return false;
  }

  const target =
    preferred?.isConnected && menu.contains(preferred)
      ? preferred
      : menu.querySelector(FOCUSABLE_MENU_ITEM);
  if (!target) return false;

  if (!consumeRestoreAttempt(menu)) return false;

  target.focus();
  return menu.contains(document.activeElement);
};

/**
 * Libera a recuperação para a próxima sessão de abertura do mesmo container.
 */
export const releaseMenuFocusBudget = menu => {
  if (menu) restoreUsed.delete(menu);
};

/**
 * Ponte usada pelos handlers `@focusout`. Fecha em saída confirmada; quando o
 * foco some, adia para ver onde ele realmente parou: dentro do menu não faz
 * nada, fora fecha, e em lugar nenhum tenta recuperar — fechando se não
 * conseguir.
 */
export const dismissMenuOnFocusOut = (event, closeMenu) => {
  const menuElement = event?.currentTarget;

  // Fechar encerra a sessão de abertura: o próximo menu exibido nesse mesmo
  // container — o do PipelineSelector nunca é recriado — começa com a
  // recuperação disponível de novo, em vez de herdar a tentativa gasta.
  const dismiss = () => {
    releaseMenuFocusBudget(menuElement);
    closeMenu();
  };

  if (focusLeftMenu(event)) {
    dismiss();
    return true;
  }

  const menu = menuElement;
  const lostFocusFrom = event?.target ?? null;
  if (typeof menu?.contains !== 'function') return false;

  queueMicrotask(() => {
    if (!menu.isConnected) return;

    // O foco saiu do documento inteiro — Tab para a barra do navegador, troca
    // de janela, outro contexto de navegação. Isso é saída deliberada: trazer
    // o foco de volta prenderia o usuário no menu.
    if (!document.hasFocus()) {
      dismiss();
      return;
    }

    const active = document.activeElement;
    if (!focusIsNowhere(active)) {
      // O foco pousou em algum lugar concreto — outro controle, um iframe,
      // outro documento. Só é saída se esse lugar estiver fora do menu.
      if (menu.contains(active)) {
        // Foco confirmado dentro: a sessão está saudável de novo e a
        // recuperação volta a ficar disponível para a próxima falha.
        releaseMenuFocusBudget(menu);
      } else {
        dismiss();
      }
      return;
    }

    if (!restoreMenuFocus(menu, lostFocusFrom)) dismiss();
  });

  return false;
};
